import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  addPlaylist as addPlaylistService,
  deletePlaylist as deletePlaylistService,
  addTrackToPlaylist as addTrackToPlaylistService,
  removeTrackFromPlaylist as removeTrackFromPlaylistService,
  updatePlaylistName as updatePlaylistNameService,
  clearPlaylistTracks as clearPlaylistTracksService
} from '../services/playlist.service';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { useOrganizationStore } from '../store/organization.store';

const PlaylistContext = createContext();

export const usePlaylists = () => useContext(PlaylistContext);

export const PlaylistProvider = ({ children, mode }) => {
  const [playlists, setPlaylists] = useState([]);
  const [focusedPlaylists, setFocusedPlaylists] = useState([]); // Array of playlist IDs
  const [selectedPlaylist, setSelectedPlaylist] = useState(null); // For filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playlistTrackIds, setPlaylistTrackIds] = useState({}); // { [playlistId]: [trackId, ...] }
  const [tracks] = useState({});
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const isPublicMode = mode === 'public';
  const auth = getAuth();
  const db = getFirestore(getApp());

  // Get current organization from the store
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force refresh the ID token to ensure Firestore has valid auth
        await firebaseUser.getIdToken(true);
      }
      setUser(firebaseUser);
      setUserLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  // Fetch playlists in real-time
  useEffect(() => {
    if (!user) {
      setPlaylists([]);
      setLoading(false);
      return;
    }

    // Public mode: playlists stored under user doc
    // Library mode: playlists stored under org
    if (isPublicMode) {
      setLoading(true);
      const playlistsRef = collection(db, 'users', user.uid, 'publicPlaylists');
      const q = query(
        playlistsRef,
        orderBy('createdAt', 'asc')
      );

      const unsub = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPlaylists(data);
        setLoading(false);
      }, (err) => {
        console.error('Error fetching public playlists:', err);
        setError(err);
        setLoading(false);
      });

      return () => unsub();
    } else {
      if (!currentOrgId) {
        setPlaylists([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const userId = user.uid;

      // Query playlists in the organization filtered by userId
      const playlistsRef = collection(db, 'organizations', currentOrgId, 'playlists');
      const q = query(
        playlistsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'asc')
      );

      const unsub = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPlaylists(data);
        setLoading(false);
      }, (err) => {
        console.error('Error fetching playlists:', err);
        setError(err);
        setLoading(false);
      });

      return () => unsub();
    }
  }, [user, db, currentOrgId, isPublicMode]);

  // Clear playlists state when organization changes (library mode only)
  useEffect(() => {
    if (!isPublicMode) {
      setFocusedPlaylists([]);
      setSelectedPlaylist(null);
      setPlaylistTrackIds({});
    }
  }, [currentOrgId, isPublicMode]);

  // Real-time listeners for playlist track IDs
  useEffect(() => {
    if (!user || playlists.length === 0) {
      setPlaylistTrackIds({});
      return;
    }

    if (!isPublicMode && !currentOrgId) {
      setPlaylistTrackIds({});
      return;
    }

    const unsubscribers = {};
    let isMounted = true;

    playlists.forEach(playlist => {
      const basePath = isPublicMode
        ? `users/${user.uid}/publicPlaylists`
        : `organizations/${currentOrgId}/playlists`;
      const tracksRef = collection(db, basePath, playlist.id, 'tracks');
      unsubscribers[playlist.id] = onSnapshot(tracksRef, snapshot => {
        if (!isMounted) return;
        setPlaylistTrackIds(prev => ({
          ...prev,
          [playlist.id]: snapshot.docs.map(doc => doc.id)
        }));
      });
    });

    // Clean up listeners on unmount or playlists change
    return () => {
      isMounted = false;
      Object.values(unsubscribers).forEach(unsub => unsub && unsub());
    };
  }, [user, playlists, db, currentOrgId, isPublicMode]);

  // Playlist CRUD actions - pass mode to service
  const addPlaylist = useCallback(async (name, description = '') => {
    if (!isPublicMode && !currentOrgId) {
      setError(new Error('No organization selected'));
      return;
    }

    // Optimistically add a temporary playlist
    const tempId = `temp-${Date.now()}`;
    const tempPlaylist = { id: tempId, name, description };
    setPlaylists(prev => [...prev, tempPlaylist]);
    setLoading(true);
    try {
      await addPlaylistService(name, description, mode);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prev => prev.filter(p => p.id !== tempId));
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrgId, isPublicMode, mode]);

  const deletePlaylist = useCallback(async (playlistId) => {
    // Optimistically remove playlist
    const prevPlaylists = playlists;
    setPlaylists(prev => prev.filter(p => p.id !== playlistId));
    setLoading(true);
    try {
      await deletePlaylistService(playlistId, mode);
      setFocusedPlaylists(focused => focused.filter(id => id !== playlistId));
      if (selectedPlaylist === playlistId) setSelectedPlaylist(null);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prevPlaylists);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlists, selectedPlaylist, mode]);

  const addTrackToPlaylist = useCallback(async (playlistId, trackId) => {
    // Optimistically update UI
    setPlaylistTrackIds(prev => ({
      ...prev,
      [playlistId]: [...(prev[playlistId] || []), trackId]
    }));
    try {
      await addTrackToPlaylistService(playlistId, trackId, mode);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({
        ...prev,
        [playlistId]: prev[playlistId].filter(id => id !== trackId)
      }));
      setError(err);
    }
  }, [mode]);

  const removeTrackFromPlaylist = useCallback(async (playlistId, trackId) => {
    // Optimistically update UI
    setPlaylistTrackIds(prev => ({
      ...prev,
      [playlistId]: (prev[playlistId] || []).filter(id => id !== trackId)
    }));
    try {
      await removeTrackFromPlaylistService(playlistId, trackId, mode);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({
        ...prev,
        [playlistId]: [...(prev[playlistId] || []), trackId]
      }));
      setError(err);
    }
  }, [mode]);

  // Add updatePlaylistName action
  const updatePlaylistName = useCallback(async (playlistId, name) => {
    // Optimistically update playlist name
    const prevPlaylists = playlists;
    setPlaylists(prev => prev.map(p => p.id === playlistId ? { ...p, name } : p));
    setLoading(true);
    try {
      await updatePlaylistNameService(playlistId, name, mode);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prevPlaylists);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlists, mode]);

  // Add clearPlaylistTracks action
  const clearPlaylistTracks = useCallback(async (playlistId) => {
    // Optimistically clear tracks
    const prevTracks = playlistTrackIds[playlistId] || [];
    setPlaylistTrackIds(prev => ({ ...prev, [playlistId]: [] }));
    setLoading(true);
    try {
      await clearPlaylistTracksService(playlistId, mode);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({ ...prev, [playlistId]: prevTracks }));
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlistTrackIds, mode]);

  // Focused playlists logic
  const toggleFocusedPlaylist = (playlistId) => {
    setFocusedPlaylists(focused =>
      focused.includes(playlistId)
        ? focused.filter(id => id !== playlistId)
        : [...focused, playlistId]
    );
  };

  // Select playlist for filtering
  const selectPlaylist = (playlistId) => {
    setSelectedPlaylist(playlistId);
  };

  // Clear all focused playlists
  const clearFocusedPlaylists = () => setFocusedPlaylists([]);

  return (
    <PlaylistContext.Provider
      value={{
        playlists,
        loading,
        error,
        addPlaylist,
        deletePlaylist,
        addTrackToPlaylist,
        removeTrackFromPlaylist,
        focusedPlaylists,
        toggleFocusedPlaylist,
        clearFocusedPlaylists,
        selectedPlaylist,
        selectPlaylist,
        setError,
        playlistTrackIds,
        updatePlaylistName,
        clearPlaylistTracks,
        tracks,
        currentOrgId, // Expose for components that need to check
        userLoading,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};
